import styled from "styled-components";
import { InputsStateSinceLastPurchase } from "./BodyPage";

const DataPart = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const CustomCard = styled.div`
  padding: .5rem;
  border: 1px solid;
  border-radius: .5rem;
  gap: 0.5rem;
  height: fit-content;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  box-sizing: border-box;
  width: 18rem;
  height: 7rem;
`;

const Bar = styled.div<{ ratio: number; fillColor?: string; baseColor?: string }>`
  width: 100%;
  height: 14px;
  position: relative;
  border-radius: 5px;
  border: 1px solid;
  overflow: hidden;

  /* base layer: represents the full bar area (e.g., deficit color for minimumStock) */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${({ baseColor }) => baseColor || '#e6e6e6'};
  }

  /* fill layer: represents the filled portion */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: ${({ ratio }) => `${ratio * 100}%`};
    height: 100%;
    background-color: ${({ fillColor }) => fillColor || 'green'};
    transition: width 200ms ease-in-out, background-color 200ms ease-in-out;
  }
`;

interface DataSectionProps {
  listData: InputsStateSinceLastPurchase[];
  selectedMetric: 'minimumStock' | 'lastPurchaseAmount';
}

function getBarInfo(item: InputsStateSinceLastPurchase, selectedMetric: 'minimumStock' | 'lastPurchaseAmount', ratio: number, safeRef: number) {
  let backgroundColor = '';
  let extraText = '';
  let barBaseColor = '';
  let barFillColor = '';

  if (selectedMetric === 'minimumStock') {
    // Alert when stock is below minimum: show deficit as baseColor (red) and fill as green up to 100%
    backgroundColor = ratio < 1 ? 'rgba(255, 99, 71, 0.08)' : '';
    barBaseColor = 'lightcoral';
    barFillColor = 'lightgreen';
    if (safeRef === 0) {
      extraText = ' — Sin mínimo definido';
    } else if (ratio < 1) {
      const faltan = Math.round(safeRef - item.stock);
      extraText = ` — Faltan ${faltan} ${item.unit}`;
    }
  } else {
    // lastPurchaseAmount: alert when stock exceeds last purchase
    backgroundColor = ratio > 1 ? 'rgba(255, 99, 71, 0.08)' : '';
    barBaseColor = '#e6e6e6';
    barFillColor = ratio > 1 ? 'lightcoral' : 'lightgreen';
    if (safeRef === 0) {
      extraText = ' — Sin dato de última compra';
    } else if (ratio > 1) {
      const exceso = Math.round(item.stock - safeRef);
      const pct = Math.round((ratio - 1) * 100);
      extraText = ` — Exceso ${exceso} ${item.unit} (+${pct}%)`;
    }
  }

  return { backgroundColor, extraText, barBaseColor, barFillColor };
}

export function DataSection({ listData, selectedMetric }: DataSectionProps) {
  return (
    <DataPart>
      {listData.length === 0 ? (
        <h3>Cargando datos...</h3>
      ) : (
        listData.map(item => {
          const fontSize = Math.min(20, 400 / item.name.length);
          const referenceValue = selectedMetric === 'minimumStock' ? item.minimumStock : item.lastPurchaseAmount;
          const safeRef = referenceValue > 0 ? referenceValue : 0;
          const ratio = safeRef > 0 ? item.stock / safeRef : 0;

          // For minimumStock we show progress towards the minimum (cap at 100%)
          const fillRatio = selectedMetric === 'minimumStock' ? Math.min(ratio, 1) : ratio;

          const { backgroundColor, extraText, barBaseColor, barFillColor } = getBarInfo(item, selectedMetric, ratio, safeRef);

          return (
            <CustomCard key={item.id} style={{ backgroundColor }}>
              <h3 style={{ fontSize: `${fontSize}px` }}>{item.name}</h3>
              <Bar ratio={fillRatio} baseColor={barBaseColor} fillColor={barFillColor}>
                {/* We use a pseudo-element for fill color; override via inline style is not possible here, so
                    we rely on CSS variables or adjust the Bar definition if needed. For now colors are set
                    by backgroundColor on the card and the default fill. */}
              </Bar>
              <span style={{ alignSelf: "flex-end", fontWeight: "bold" }}>{item.stock} / {referenceValue} {item.unit}{extraText}</span>
            </CustomCard>
          );
        })
      )}
    </DataPart >
  );
}